import { CommonModule } from "@angular/common";
import { Component, Input } from "@angular/core";
import { AbstractControl } from "@angular/forms";

/**
 * Inline "soft-warn" hint shown under an empty optional control.
 *
 * Usage:
 *   <tui-input formControlName="guardianNIC">Client NIC</tui-input>
 *   <app-recommended-hint
 *     [control]="form.get('guardianNIC')"
 *     message="Recommended. You can save without it and update later."
 *   ></app-recommended-hint>
 *
 * - The hint renders only when the control has been touched and has no value.
 * - It does NOT block submission. It is meant for fields that the data-entry
 *   user is allowed to skip but really should fill in eventually.
 */
@Component({
  selector: "app-recommended-hint",
  standalone: true,
  imports: [CommonModule],
  template: `
    <p *ngIf="shouldShow" class="recommended-hint">
      <span class="recommended-hint__icon" aria-hidden="true">!</span>
      <span class="recommended-hint__text">{{ message }}</span>
    </p>
  `,
  styles: [
    `
      .recommended-hint {
        display: flex;
        align-items: center;
        gap: 0.4rem;
        margin: 0.25rem 0 0;
        font-size: 0.75rem;
        line-height: 1.1rem;
        color: #8a6d1a;
      }
      .recommended-hint__icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 1rem;
        height: 1rem;
        border-radius: 9999px;
        background-color: #fde68a;
        color: #92400e;
        font-size: 0.7rem;
        font-weight: 700;
        flex-shrink: 0;
      }
      .recommended-hint__text {
        font-weight: 500;
      }
    `,
  ],
})
export class RecommendedHintComponent {
  @Input() control: AbstractControl | null = null;
  @Input() message =
    "Recommended. You can save without it and update later.";
  /**
   * If true, always show the hint (e.g. for FormArrays where
   * `control.value` is `[]` and would always be considered "empty").
   * Use when the parent component controls visibility itself.
   */
  @Input() alwaysShow = false;

  get shouldShow(): boolean {
    if (this.alwaysShow) return true;
    const c = this.control;
    if (!c) return false;
    if (!c.touched && !c.dirty) return false;
    return this.isEmpty(c.value);
  }

  private isEmpty(value: unknown): boolean {
    if (value === null || value === undefined) return true;
    if (typeof value === "string") return value.trim().length === 0;
    if (Array.isArray(value)) return value.length === 0;
    return false;
  }
}
